import React, { createContext, useContext, useMemo } from 'react';

import { createReportGridColDef } from 'src/components/reports/helpers';
import { Apollo } from 'src/gql/generated';

export interface ColumnOption {
  displayName: string;
  id: string;
}

interface ReportColumnMentionContextValue {
  columns: ColumnOption[];
  reportId: UUID;
}

const ReportColumnMentionCtx = createContext<ReportColumnMentionContextValue | null>(null);

export function useReportColumnMentionContext(): ReportColumnMentionContextValue {
  const ctx = useContext(ReportColumnMentionCtx);
  if (!ctx)
    throw new Error(
      'useReportColumnMentionContext must be used within ReportColumnMentionProvider'
    );
  return ctx;
}

interface ReportColumnMentionProviderProps {
  children: React.ReactNode;
  reportId: UUID;
}

export function ReportColumnMentionProvider({
  children,
  reportId,
}: ReportColumnMentionProviderProps): JSX.Element {
  const { data } = Apollo.useGetReportInitialStateQuery({ variables: { reportId } });
  const columns = useMemo(() => {
    const allOptions = data?.report?.columnOptions ?? [];
    const visibilityModel = data?.report?.state?.columns?.columnVisibilityModel;

    const builtInColumns: ColumnOption[] = [];
    const extractionColumns: ColumnOption[] = [];

    for (const o of allOptions) {
      switch (o.__typename) {
        case 'ExtractionReportColumn': {
          const isVisible = !visibilityModel || visibilityModel[o.questionId] === true;
          if (!isVisible) continue;
          const colDef = createReportGridColDef(o);
          extractionColumns.push({
            displayName: colDef.headerName ?? '',
            id: o.questionId,
          });
          break;
        }
        case 'FileFieldColumn': {
          const isVisible = !visibilityModel || visibilityModel[o.field] === true;
          if (!isVisible) continue;
          builtInColumns.push({ displayName: o.displayName, id: o.field });
          break;
        }
      }
    }

    return [...builtInColumns, ...extractionColumns].sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  }, [data?.report?.columnOptions, data?.report?.state?.columns?.columnVisibilityModel]);
  const value = useMemo(() => ({ columns, reportId }), [columns, reportId]);

  return (
    <ReportColumnMentionCtx.Provider value={value}>{children}</ReportColumnMentionCtx.Provider>
  );
}
